<template>
  <div class="bg-utel-v2-secondary p-5 mb-2 mt-3 pt-4 rounded" id="agregar_administrador">
    <h4 class="title-secondary">Agregar administrador</h4>
    <small>Completa el formulario para agregar un nuevo administrador</small>
    
    <form @submit.prevent="handleSubmit" class="mt-4">
      <div class="row justify-content-center">
        <div class="col-md-6">
          <!-- Client Selection -->
          <div v-if="clients.length > 0" class="form-group mb-4">
            <label for="client" class="form-label">Seleccione un cliente</label>
            <select v-model="selectedClientId" class="form-control rounded-8 sin-borde py-2" required>
              <option value="">Seleccionar el cliente</option>
              <option v-for="client in clients" :key="client.id" :value="client.id">
                {{ client.name }}
              </option>
            </select>
          </div>

          <!-- Role Selection -->
          <div class="form-group mb-4">
            <label for="rol" class="form-label">Rol del administrador</label>
            <select v-model="formData.rol" class="form-control rounded-8 sin-borde py-2" required>
              <option value="">Seleccionar rol</option>
              <option value="admin">Administrador</option>
              <option value="superadmin" v-if="issuperadmin">Super Administrador</option>
            </select>
          </div>

          <!-- Form Fields -->
          <div class="form-group mb-4">
            <input 
              v-model="formData.firstname" 
              type="text" 
              class="form-control rounded-8 sin-borde"
              :class="{ 'is-invalid': validation.firstname.error }"
              placeholder="Nombre del administrador" 
              required
            />
            <div v-if="validation.firstname.error" class="invalid-feedback">
              {{ validation.firstname.message }}
            </div>
          </div>

          <div class="form-group mb-4">
            <input 
              v-model="formData.lastname" 
              type="text" 
              class="form-control rounded-8 sin-borde"
              :class="{ 'is-invalid': validation.lastname.error }"
              placeholder="Apellido del administrador" 
              required
            />
            <div v-if="validation.lastname.error" class="invalid-feedback">
              {{ validation.lastname.message }}
            </div>
          </div>

          <div class="form-group mb-4">
            <input 
              v-model="formData.email" 
              type="email" 
              class="form-control rounded-8 sin-borde"
              :class="{ 'is-invalid': validation.email.error }"
              placeholder="Email del administrador" 
              @blur="validateEmail"
              required
            />
            <div v-if="validation.email.error" class="invalid-feedback">
              {{ validation.email.message }}
            </div>
          </div>

          <div class="form-group mb-4">
            <input 
              v-model="formData.position" 
              type="text" 
              class="form-control rounded-8 sin-borde"
              placeholder="Cargo" 
            />
          </div>

          <div class="form-group mb-4">
            <input 
              ref="phoneInput"
              v-model="formData.cellphone" 
              type="tel" 
              class="form-control rounded-8 sin-borde"
              :class="{ 'is-invalid': validation.cellphone.error }"
              placeholder="Celular"
              @input="validatePhone"
            />
            <div v-if="validation.cellphone.error" class="invalid-feedback">
              {{ validation.cellphone.message }}
            </div>
          </div>

          <div class="d-flex gap-3 justify-content-end">
            <button type="button" @click="resetForm" class="btn bg-utel-v2-secondary text-primary rounded-8">
              Cancelar
            </button>
            <button type="submit" :disabled="!isFormValid || isSubmitting" class="btn btn-primary rounded-8">
              {{ isSubmitting ? 'Procesando...' : 'Guardar administrador' }}
            </button>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_MIN_LENGTH = 9;

export default {
  name: 'CreateUserForm',
  
  props: {
    clients: { type: Array, default: () => [] },
    urlvalidateemail: { type: String, default: '' },
    url: { type: String, default: '' },
    token: { type: String, default: '' },
    issuperadmin: { type: Boolean, default: false }
  },
  
  emits: ['user-created', 'form-reset'],
  
  data() {
    return {
      selectedClientId: '',
      formData: this.getEmptyUser(),
      phoneIti: null,
      isSubmitting: false,
      validation: {
        firstname: { error: false, message: '' },
        lastname: { error: false, message: '' },
        email: { error: false, message: '' },
        cellphone: { error: false, message: '' }
      }
    };
  },
  
  computed: {
    isFormValid() {
      return this.formData.firstname && 
             this.formData.lastname && 
             this.formData.email && 
             EMAIL_REGEX.test(this.formData.email) &&
             this.formData.cellphone &&
             this.formData.cellphone.length >= PHONE_MIN_LENGTH &&
             this.formData.rol &&
             this.selectedClientId;
    }
  },
  
  mounted() {
    this.$nextTick(() => {
      this.initPhone();
    });
  },
  
  methods: {
    getEmptyUser() {
      return {
        firstname: '',
        lastname: '',
        email: '',
        cellphone: '',
        position: '',
        rol: ''
      };
    },
    
    async handleSubmit() {
      if (!this.isFormValid || this.isSubmitting) return;
      
      this.isSubmitting = true;
      
      try {
        const userData = { 
          ...this.formData,
          client_id: this.selectedClientId,
          cellphone: this.phoneIti ? this.phoneIti.getNumber() : this.formData.cellphone
        };
        
        const response = await axios.post(this.url, userData, {
          headers: {
            'Authorization': `Bearer ${this.token}`,
            'Content-Type': 'application/json'
          }
        });
        
        this.$emit('user-created', { ...this.formData });
        
        this.$swal.fire({
          title: 'Éxito!',
          text: 'Administrador creado correctamente.',
          icon: 'success'
        });
        
        this.resetForm();
        
      } catch (error) {
        console.error('Error creating user:', error);
        
        const errors = error.response?.data?.errors || {};
        this.handleValidationErrors(errors);
        
        this.$swal.fire({
          title: 'Error!',
          text: error.response?.data?.message || 'Hubo un error al crear el administrador.',
          icon: 'error'
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    
    handleValidationErrors(errors) {
      Object.keys(this.validation).forEach(field => {
        this.validation[field] = {
          error: !!errors[field],
          message: errors[field] ? errors[field][0] : ''
        };
      });
    },
    
    async validateEmail() {
      const email = this.formData.email;
      
      if (!email) {
        this.validation.email = { error: true, message: 'El email es requerido' };
        return;
      }
      
      if (!EMAIL_REGEX.test(email)) {
        this.validation.email = { error: true, message: 'El formato del email no es válido' };
        return;
      }
      
      try {
        const response = await axios.post(this.urlvalidateemail, { email }, {
          headers: {
            'Authorization': `Bearer ${this.token}`
          }
        });
        
        if (!response.data) {
          this.validation.email = { error: true, message: 'El email ya está registrado' };
        } else {
          this.validation.email = { error: false, message: '' };
        }
      } catch (error) {
        console.error('Error validating email:', error);
      }
    },
    
    validatePhone() {
      const phone = this.formData.cellphone;
      if (phone.length < PHONE_MIN_LENGTH) {
        this.validation.cellphone = { 
          error: true, 
          message: `El número debe tener al menos ${PHONE_MIN_LENGTH} dígitos` 
        };
      } else {
        this.validation.cellphone = { error: false, message: '' };
      }
    },
    
    initPhone() {
      if (window.intlTelInput && this.$refs.phoneInput) {
        this.phoneIti = window.intlTelInput(this.$refs.phoneInput, {
          utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js',
          initialCountry: 'mx',
          separateDialCode: true,
          hiddenInput: 'full'
        });
      }
    },
    
    resetForm() {
      this.formData = this.getEmptyUser();
      this.selectedClientId = '';
      Object.keys(this.validation).forEach(field => {
        this.validation[field] = { error: false, message: '' };
      });
      this.$emit('form-reset');
    }
  }
}
</script>

<style scoped>
.title-secondary {
  color: #494949;
  font-weight: bold;
}

.form-label {
  color: #1A0054;
  font-weight: 500;
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875em;
  color: #dc3545;
}
</style>